/**
 * Admin: Self-service Onboarding Tokens
 * =====================================
 * Generate, copy, share, and revoke tokenised onboarding links. Surfaces the
 * downstream customer + watchlist alert/case once the prospect submits.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Link2, Plus, Loader2, Copy, Check, X, Trash2,
  Clock, CheckCircle2, AlertTriangle, ShieldAlert, ExternalLink,
  Mail, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const API = `${window.location.origin}/api/onboarding`;

const STATUS_COLORS = { active: "#22c55e", used: "#2563eb", expired: "#f59e0b", revoked: "#94a3b8" };

const inputStyle = { width: "100%", background: "#080c12", border: "1px solid #1e2530", borderRadius: 6, padding: "8px 12px", color: "#f1f5f9", fontSize: 12 };
const btnPrimary = { background: "linear-gradient(135deg, #2563eb, #1d4ed8)", border: "none", borderRadius: 6, color: "white", padding: "8px 18px", fontWeight: 600, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 };
const btnGhost = { background: "transparent", border: "1px solid #1e2530", borderRadius: 6, color: "#94a3b8", padding: "8px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 };

function StatCard({ label, value, color }) {
  return (
    <div style={{ background: "#0d1117", border: `1px solid ${color}33`, borderRadius: 10, padding: 14 }}>
      <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>{label}</div>
      <div style={{ color, fontSize: 24, fontWeight: 700 }}>{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || "#64748b";
  return (
    <span style={{ background: `${c}22`, color: c, border: `1px solid ${c}55`, fontSize: 10, padding: "2px 8px", borderRadius: 4, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>{status}</span>
  );
}

function CreateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ customer_label: "", customer_type: "individual", prefill_name: "", prefill_email: "", expires_in_days: 7 });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/tokens`, form, { withCredentials: true });
      toast.success("Onboarding link created");
      onCreated(data); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create"); }
    finally { setSaving(false); }
  };
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 12, padding: 24, width: "100%", maxWidth: 540 }} data-testid="create-token-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 style={{ color: "#f1f5f9", fontSize: 16, fontWeight: 700, margin: 0 }}>Generate Onboarding Link</h3>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: "#94a3b8", cursor: "pointer" }}><X size={18} /></button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div style={{ gridColumn: "1 / -1" }}>
            <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Label (internal)</div>
            <input data-testid="token-label" type="text" value={form.customer_label} onChange={(e) => setForm({ ...form, customer_label: e.target.value })} style={inputStyle} placeholder="e.g. Acme Industries KYC – Jan 2026" />
          </div>
          <div>
            <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Type</div>
            <select data-testid="token-type" value={form.customer_type} onChange={(e) => setForm({ ...form, customer_type: e.target.value })} style={inputStyle}>
              <option value="individual">Individual</option>
              <option value="corporate">Corporate</option>
            </select>
          </div>
          <div>
            <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Expires (days)</div>
            <input data-testid="token-expiry" type="number" min={1} max={90} value={form.expires_in_days} onChange={(e) => setForm({ ...form, expires_in_days: parseInt(e.target.value) || 7 })} style={inputStyle} />
          </div>
          <div>
            <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Prefill Name (optional)</div>
            <input type="text" value={form.prefill_name} onChange={(e) => setForm({ ...form, prefill_name: e.target.value })} style={inputStyle} placeholder="Shows in the form" />
          </div>
          <div>
            <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Prefill Email (optional)</div>
            <input type="email" value={form.prefill_email} onChange={(e) => setForm({ ...form, prefill_email: e.target.value })} style={inputStyle} />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-4">
          <button onClick={onClose} style={btnGhost}>Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="create-token-submit" style={btnPrimary}>
            {saving ? <Loader2 className="animate-spin" size={12} /> : <Plus size={12} />}
            {saving ? "Generating..." : "Generate Link"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CopyBtn({ text, testId }) {
  const [done, setDone] = useState(false);
  const click = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true); toast.success("Link copied"); setTimeout(() => setDone(false), 1500);
    } catch { toast.error("Copy failed"); }
  };
  return (
    <button onClick={click} data-testid={testId} style={{ background: done ? "rgba(34,197,94,0.10)" : "rgba(37,99,235,0.10)", border: `1px solid ${done ? "#22c55e" : "#2563eb"}`, borderRadius: 4, color: done ? "#22c55e" : "#2563eb", padding: "4px 8px", fontSize: 10, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
      {done ? <Check size={11} /> : <Copy size={11} />} {done ? "Copied" : "Copy"}
    </button>
  );
}

export default function OnboardingTokensPage() {
  const [tokens, setTokens] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [filter, setFilter] = useState("");
  const [newest, setNewest] = useState(null); // most recently created (for highlight)

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter) params.status = filter;
      const [t, s] = await Promise.all([
        axios.get(`${API}/tokens`, { params, withCredentials: true }),
        axios.get(`${API}/stats`, { withCredentials: true }),
      ]);
      setTokens(t.data.tokens || []); setStats(s.data || {});
    } catch (e) { toast.error("Failed to load tokens"); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const onCreated = (tok) => { setNewest(tok); load(); };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this onboarding link? It cannot be re-activated.")) return;
    try { await axios.delete(`${API}/tokens/${id}`, { withCredentials: true }); toast.success("Revoked"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const buildPortalUrl = (t) => `${window.location.origin}/onboarding/${t.token}`;
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString() : "—";

  return (
    <div data-testid="onboarding-page" style={{ color: "#f1f5f9" }}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <Link2 style={{ width: 26, height: 26, color: "#2563eb" }} />
          <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0, color: "#f1f5f9" }} data-testid="page-title">Self-Service Onboarding</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} style={btnGhost}><RefreshCw size={12} /> Refresh</button>
          <button onClick={() => setShowCreate(true)} data-testid="new-token-btn" style={btnPrimary}><Plus size={12} /> New Link</button>
        </div>
      </div>
      <p style={{ color: "#94a3b8", fontSize: 13, marginBottom: 18 }}>
        Generate tokenised links and send to prospects. They self-onboard; we screen against the India Watchlist instantly and surface matches as alerts + auto-cases.
      </p>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 18 }}>
        <StatCard label="Total" value={stats.total || 0} color="#2563eb" />
        <StatCard label="Active" value={stats.active || 0} color="#22c55e" />
        <StatCard label="Submitted" value={stats.used || 0} color="#06b6d4" />
        <StatCard label="Expired" value={stats.expired || 0} color="#f59e0b" />
        <StatCard label="Revoked" value={stats.revoked || 0} color="#94a3b8" />
      </div>

      {/* Newly-created link banner */}
      {newest && (
        <div style={{ background: "linear-gradient(135deg, #1e3a5f, #1e2d45)", border: "1px solid #2563eb55", borderRadius: 10, padding: 16, marginBottom: 16 }} data-testid="newest-banner">
          <div className="flex items-center justify-between">
            <div>
              <div style={{ color: "#cbd5e1", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>New onboarding link ready · expires {fmtDate(newest.expires_at)}</div>
              <code style={{ color: "#f1f5f9", fontSize: 13, fontFamily: "ui-monospace, monospace", background: "#080c12", padding: "8px 12px", borderRadius: 6, border: "1px solid #1e2530", display: "inline-block" }}>
                {buildPortalUrl(newest)}
              </code>
            </div>
            <div className="flex items-center gap-2">
              <CopyBtn text={buildPortalUrl(newest)} testId="copy-new-link" />
              <a href={`mailto:${newest.prefill?.email || ""}?subject=KYC%20Onboarding%20—%20${encodeURIComponent(newest.customer_label || "")}&body=${encodeURIComponent("Please complete your KYC onboarding using the secure link below:\n\n" + buildPortalUrl(newest) + "\n\nLink expires " + newest.expires_at + ".")}`}
                target="_blank" rel="noopener noreferrer" style={{ ...btnGhost, textDecoration: "none" }}>
                <Mail size={12} /> Send Email
              </a>
              <a href={buildPortalUrl(newest)} target="_blank" rel="noopener noreferrer" style={{ ...btnGhost, textDecoration: "none" }}>
                <ExternalLink size={12} /> Open
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 mb-3">
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="status-filter" style={{ ...inputStyle, width: 200 }}>
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="used">Submitted</option>
          <option value="expired">Expired</option>
          <option value="revoked">Revoked</option>
        </select>
      </div>

      {/* Table */}
      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ background: "#080c12" }}>
            <Th>Status</Th><Th>Label</Th><Th>Type</Th><Th>Created</Th><Th>Expires</Th><Th>Result</Th><Th>Actions</Th>
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ textAlign: "center", padding: 30, color: "#64748b" }}><Loader2 className="animate-spin inline-block" size={16} /> Loading…</td></tr>
            ) : tokens.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: "center", padding: 40, color: "#64748b", fontSize: 13 }}>
                No onboarding links yet. Click <strong style={{ color: "#2563eb" }}>"New Link"</strong> to generate one.
              </td></tr>
            ) : tokens.map((t) => (
              <tr key={t.id} style={{ borderTop: "1px solid #1e2530" }} data-testid={`token-row-${t.id}`}>
                <Td><StatusBadge status={t.status} /></Td>
                <Td><strong style={{ color: "#f1f5f9", fontSize: 12 }}>{t.customer_label || <span style={{ color: "#64748b" }}>(unlabelled)</span>}</strong></Td>
                <Td><span style={{ color: "#94a3b8", fontSize: 11, textTransform: "capitalize" }}>{t.customer_type}</span></Td>
                <Td><span style={{ color: "#94a3b8", fontSize: 11 }}>{fmtDate(t.created_at)}</span></Td>
                <Td><span style={{ color: t.status === "expired" ? "#f59e0b" : "#94a3b8", fontSize: 11 }}>{fmtDate(t.expires_at)}</span></Td>
                <Td>
                  {t.status === "used" ? (
                    <div className="flex items-center gap-2">
                      <Link to={`/customers/${t.customer_id}`} style={{ color: "#06b6d4", fontSize: 11, textDecoration: "underline" }}>customer</Link>
                      {t.match_score != null && (
                        <span style={{ color: t.match_score >= 88 ? "#dc2626" : "#f59e0b", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>
                          <ShieldAlert size={10} /> {t.match_score}% match
                        </span>
                      )}
                    </div>
                  ) : <span style={{ color: "#64748b", fontSize: 11 }}>—</span>}
                </Td>
                <Td>
                  <div className="flex items-center gap-1">
                    {t.status === "active" && <CopyBtn text={buildPortalUrl(t)} testId={`copy-${t.id}`} />}
                    {t.status === "active" && (
                      <a href={buildPortalUrl(t)} target="_blank" rel="noopener noreferrer" data-testid={`open-${t.id}`} style={{ background: "rgba(148,163,184,0.10)", border: "1px solid #94a3b8", borderRadius: 4, color: "#94a3b8", padding: "4px 8px", fontSize: 10, cursor: "pointer", textDecoration: "none", display: "flex", alignItems: "center", gap: 4 }}>
                        <ExternalLink size={10} />
                      </a>
                    )}
                    {(t.status === "active" || t.status === "expired") && (
                      <button onClick={() => revoke(t.id)} data-testid={`revoke-${t.id}`} title="Revoke" style={{ background: "rgba(239,68,68,0.10)", border: "1px solid #ef4444", borderRadius: 4, color: "#ef4444", padding: "4px 8px", fontSize: 10, cursor: "pointer" }}>
                        <Trash2 size={10} />
                      </button>
                    )}
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreated={onCreated} />}
    </div>
  );
}

function Th({ children }) { return <th style={{ textAlign: "left", padding: "10px 12px", color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{children}</th>; }
function Td({ children }) { return <td style={{ padding: "10px 12px", verticalAlign: "middle" }}>{children}</td>; }

function buildPortalUrl(t) { return `${window.location.origin}/onboarding/${t.token}`; }
